// Card — surface container for content groups. Mirrors `.card` and its
// variants in /Users/harry/Downloads/employee-portal/project/styles.css
// (lines 253–267):
//
//   .card             — neutral.25 bg, 12px radius, 24px pad, elevLow shadow
//   .card--flush      — padding: 0 (used when the card hosts a flush table)
//   .card--tasks      — neutral.25 → lavender.25 gradient bg
//   .card--luna       — luna gradientCard (subtle tri-stop wash)
//
// The tasks variant in the design also features a fixed-height inner scroll
// region (max-height 264px, thin scrollbar). That belongs to `.inner-table`,
// not `.card--tasks` itself, so it's owned by the consumer.
//
// `bgTasksCard` and `luna.gradientCard` are defined as Chakra color tokens in
// theme.ts. Because Chakra's `bg` prop will resolve a color token to a CSS
// value, both gradient strings come through correctly via `bg="bgTasksCard"`
// and `bg="luna.gradientCard"`. We keep the inline literal as a backstop in
// case the token is changed shape in the future.

import { Box, BoxProps } from "@chakra-ui/react";
import { ReactNode } from "react";

export type CardVariant = "default" | "flush" | "tasks" | "luna";

export interface CardProps extends Omit<BoxProps, "variant"> {
  variant?: CardVariant;
  children: ReactNode;
}

// design ref: styles.css:260 — `.card--tasks` exact gradient.
const TASKS_GRADIENT =
  "linear-gradient(90deg, #FCFDFD 0%, #ECEEFF 100%)";

// design ref: styles.css:263–267 — `.card--luna` layered gradient stack on top
// of `--backgrounds-container` (neutral.25). Mirrors `luna.gradientCard` in
// theme.ts but composited over neutral.25 to match the CSS exactly.
const LUNA_GRADIENT =
  "linear-gradient(91deg, rgba(252, 253, 253, 0.10) 47.21%, rgba(237, 111, 111, 0.10) 75.78%, rgba(113, 122, 247, 0.10) 99.79%), #FCFDFD";

const Card = ({ variant = "default", children, ...rest }: CardProps) => {
  // design ref: styles.css:253–258 — base `.card`.
  const baseProps: BoxProps = {
    borderRadius: "component.2xl", // 12px
    p: "1xl", // 24px
    boxShadow: "elevLow",
    bg: "neutral.25",
  };

  const variantProps: BoxProps =
    variant === "flush"
      ? { p: 0 }
      : variant === "tasks"
        ? { bg: TASKS_GRADIENT }
        : variant === "luna"
          ? { bg: LUNA_GRADIENT }
          : {};

  return (
    <Box {...baseProps} {...variantProps} {...rest}>
      {children}
    </Box>
  );
};

export default Card;
